import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Platform, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSharedValue } from 'react-native-reanimated';
import { ICarouselInstance } from 'react-native-reanimated-carousel';
import { CarouselRenderItemInfo } from 'react-native-reanimated-carousel/lib/typescript/types';
import { useDispatch } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';
import debounce from 'lodash.debounce';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    GALLERY_LEFT_ARROW_BUTTON_ID,
    GALLERY_RIGHT_ARROW_BUTTON_ID,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { ModularScreens, RouteNavigationParams } from 'router/types';

import { StreamTypes } from 'store/player/actions.types';
import { MixedItem } from 'store/railsBuilder/reducer.types';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import { COLOR } from 'utils/colors';
import getDeepLinkProviderValues from 'utils/deeplinks/getDeepLinkProviderValues';
import { isPhone, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import styles from 'components/Gallery/styles';
import PhoneView from 'components/Gallery/сomponents/PhoneView';
import TabletView from 'components/Gallery/сomponents/TabletView';
import PaginationDots from 'components/PaginationDots';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

import GalleryCarousel from './сomponents/GalleryCarousel';
import WebArrowButton from './сomponents/WebArrowButton';

type Props = {
    items: MixedItem[];
    navigation: StackNavigationProp<
        RouteNavigationParams,
        ModularScreens | SCREEN.DETAILS | SCREEN.SEARCH
    >;
    testID?: string;
    screenFocused: boolean;
    screenReaderEnabled: boolean;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
} & OnUavAnalyticsProps &
    StandardUavProps;

const MAX_DOTS_COUNT = 5;

const {
    ACCESSIBILITY: {
        GALLERY: {
            ACCESSIBILITY_GALLERY_SWITCH_TO_NEXT_ASSET,
            ACCESSIBILITY_GALLERY_SWITCH_TO_PREVIOUS_ASSET,
        },
    },
} = LANGUAGE_KEYS;

function Gallery({
    items,
    navigation,
    onRailsSelection,
    onDeepLinkTriggered,
    onThirdPartyAppStart,
    railId,
    templateId,
    testID,
    screenFocused,
    screenReaderEnabled,
    onShowPinDialog,
    onSetParentalControlCheckedCallback,
}: Props) {
    const galleryRef = useRef<ICarouselInstance>(null);
    const dispatch = useDispatch();
    const galleryProgress = useSharedValue(0);
    const { t } = useTranslation();

    const [autoPlay, setAutoPlay] = useState(screenFocused && items.length > 1);
    const [isGalleryActive, setIsGalleryActive] = useState(
        screenFocused && items.length > 1,
    );
    const debouncedSetAutoPlay = useCallback(debounce(setAutoPlay, 100), []);
    const isWebEdgeCase = isWeb && items.length === 2;

    useEffect(() => {
        setAutoPlay(screenFocused && !!items.length && items.length > 1);
    }, [screenFocused, items.length]);

    const handlePress = useCallback(
        (item: MixedItem, index: number) => {
            const { id, playbackType, type, detailsPageParams } = item;
            const { deeplinkProviderId, providerResourceId } =
                getDeepLinkProviderValues(item);

            onRailsSelection?.({
                id,
                playbackType: StreamTypes.catchup,
                deeplinkProviderId,
                providerResourceId,
                index,
            });

            if (detailsPageParams) {
                dispatch(
                    startSentryTransaction(
                        SENTRY_TRANSACTIONS.DetailPageRendering,
                    ),
                );
                navigation.navigate(SCREEN.DETAILS, {
                    railId,
                    templateId,
                    playbackType,
                    type,
                    ...detailsPageParams,
                });
            }
        },
        [navigation, onRailsSelection, railId, templateId, dispatch],
    );

    const handleProgressChange = useCallback((_, absoluteProgress) => {
        if (galleryRef.current && galleryRef.current.getCurrentIndex() === 0) {
            galleryProgress.value = 0;
        } else if (isWebEdgeCase) {
            galleryProgress.value = absoluteProgress % items.length;
        } else {
            galleryProgress.value = absoluteProgress;
        }
    }, []);

    const renderItem = useCallback(
        ({ item, index }: CarouselRenderItemInfo<MixedItem>) =>
            isTabletOrWeb ? (
                <TabletView
                    item={item}
                    focused={
                        screenReaderEnabled ||
                        galleryRef.current?.getCurrentIndex() === index
                    }
                    screenReaderEnabled={screenReaderEnabled}
                    onPress={handlePress}
                    onThirdPartyAppStart={onThirdPartyAppStart}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    railId={railId}
                    templateId={templateId}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    index={index}
                    totalNumberOfItems={items.length}
                    isWebEdgeCase={isWebEdgeCase}
                />
            ) : (
                <PhoneView
                    item={item}
                    focused={
                        screenReaderEnabled ||
                        galleryRef.current?.getCurrentIndex() === index
                    }
                    onPress={handlePress}
                    onThirdPartyAppStart={onThirdPartyAppStart}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    railId={railId}
                    templateId={templateId}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    index={index}
                    totalNumberOfItems={items.length}
                />
            ),
        [
            handlePress,
            railId,
            templateId,
            screenReaderEnabled,
            onThirdPartyAppStart,
            onRailsSelection,
            onDeepLinkTriggered,
            onShowPinDialog,
            onSetParentalControlCheckedCallback,
        ],
    );

    const handleBwdButtonPress = useCallback(() => {
        setAutoPlay(false);

        galleryRef.current?.prev();

        debouncedSetAutoPlay(true);
    }, [debouncedSetAutoPlay]);

    const handleFwdButtonPress = useCallback(() => {
        setAutoPlay(false);

        galleryRef.current?.next();

        debouncedSetAutoPlay(true);
    }, [debouncedSetAutoPlay]);

    const renderPagination = useCallback(
        () => (
            <View
                style={[
                    gStyles.flexRow,
                    isWeb && styles.webPaginationContainer,
                ]}
                testID={testID}
                accessible={false}
                dataSet={{ class: 'gallery_container' }}
            >
                <WebArrowButton
                    onPress={handleBwdButtonPress}
                    style={styles.bwdButton}
                    tabIndex={0}
                    testID={GALLERY_LEFT_ARROW_BUTTON_ID}
                    accessibilityLabel={t(
                        ACCESSIBILITY_GALLERY_SWITCH_TO_PREVIOUS_ASSET,
                    )}
                />
                <PaginationDots
                    activeDotColor={COLOR.WHITE}
                    galleryProgress={galleryProgress}
                    length={
                        items.length > MAX_DOTS_COUNT
                            ? MAX_DOTS_COUNT
                            : items.length
                    }
                    size={8}
                />
                <WebArrowButton
                    onPress={handleFwdButtonPress}
                    tabIndex={0}
                    testID={GALLERY_RIGHT_ARROW_BUTTON_ID}
                    accessibilityLabel={t(
                        ACCESSIBILITY_GALLERY_SWITCH_TO_NEXT_ASSET,
                    )}
                />
            </View>
        ),
        [
            items,
            handleBwdButtonPress,
            handleFwdButtonPress,
            galleryProgress,
            t,
            testID,
        ],
    );

    const handleHoverEvent = useCallback(
        (event) => {
            setIsGalleryActive(items.length > 1 && event.type !== 'mouseover');
        },
        [items.length],
    );

    useEffect(() => {
        if (isWeb) {
            const carouselContainers = document.querySelectorAll(
                '[data-class="gallery_container"]',
            );

            carouselContainers?.forEach((carouselContainerView) => {
                carouselContainerView?.addEventListener(
                    'mouseover',
                    handleHoverEvent,
                );
                carouselContainerView?.addEventListener(
                    'mouseout',
                    handleHoverEvent,
                );
            });
        }

        return () => {
            if (isWeb) {
                const carouselContainers = document.querySelectorAll(
                    '[data-class="gallery_container"]',
                );

                carouselContainers?.forEach((carouselContainerView) => {
                    carouselContainerView?.removeEventListener(
                        'mouseover',
                        handleHoverEvent,
                    );
                    carouselContainerView?.removeEventListener(
                        'mouseout',
                        handleHoverEvent,
                    );
                });
            }
        };
    }, [handleHoverEvent]);

    return (
        <>
            <View style={styles.galleryPlaceholder} />
            <View
                style={styles.absolutePosition}
                dataSet={{ class: 'gallery_container' }}
            >
                <GalleryCarousel
                    items={items}
                    renderItem={renderItem}
                    screenReaderEnabled={screenReaderEnabled}
                    autoPlay={autoPlay}
                    isGalleryActive={isGalleryActive}
                    onProgressChange={handleProgressChange}
                    ref={galleryRef}
                />
            </View>
            <View
                style={[
                    styles.paginationDotContainer,
                    isPhone && styles.phoneDotsContainer,
                    isWeb && styles.webDotsContainer,
                ]}
                pointerEvents={Platform.select({
                    web: 'auto',
                    default: 'none',
                })}
            >
                {items.length > 1 && renderPagination()}
            </View>
        </>
    );
}

export default Gallery;
